// src/pages/Login.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";
import { useMsal } from "@azure/msal-react";
import { updateFavicon, updatePageTitle } from "../utils/favicon";
import apiService from "../services/apiService";
import "./styles/Login.css";

function Login() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const isLoggingIn = useRef(false); // Prevent multiple login attempts

  // Redirect to home if already logged in - only when auth loading is complete
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/home");
    }
  }, [user, authLoading, navigate]);

  // Update favicon/title when organization data changes
  useEffect(() => {
    const organization = user?.user?.organization;
    if (organization) {
      if (organization.favicon_url) {
        updateFavicon(organization.favicon_url);
      }
      if (organization.name) {
        updatePageTitle(organization.name, "Login");
      }
    }
  }, [user?.user?.organization]);

  const handleLogin = async () => {
    // Prevent multiple login attempts
    if (isLoggingIn.current || isLoading) return;
    
    if (!termsAccepted) {
      setError("Please accept the Terms of Use and Privacy Policy");
      return;
    }

    isLoggingIn.current = true;
    setIsLoading(true);
    setError("");

    try {
      // Get Microsoft login
      const loginResponse = await instance.loginPopup({
        scopes: ["openid", "profile", "email"],
      });

      if (!loginResponse?.idToken) {
        setError("Login cancelled or failed");
        isLoggingIn.current = false;
        setIsLoading(false);
        return;
      }

      // Send token to backend
      const response = await apiService.loginWithMicrosoft(loginResponse.idToken);

      if (response.status === 200 && response.data) {
        // Update UI with organization data
        const organization = response.data.user?.organization;
        if (organization?.favicon_url) {
          updateFavicon(organization.favicon_url);
        }
        if (organization?.name) {
          updatePageTitle(organization.name);
        }

        // Store user data - this will trigger redirect via useEffect
        login(response.data);
        // Navigate after state update
        setTimeout(() => {
          navigate("/home");
        }, 100);
      } else {
        setError("Authentication failed. Please try again.");
      }
    } catch (error) {
      console.error("Login error:", error);
      
      // Handle specific error cases
      if (error.message?.includes("canceled") || error.message?.includes("cancelled")) {
        setError("Login was cancelled. Please try again.");
      } else if (error.message?.includes("popup")) {
        setError("Popup was blocked. Please allow popups for this site.");
      } else {
        setError(error.message || "Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
      isLoggingIn.current = false;
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="login-container">
        <div className="login-loading">Loading...</div>
      </div>
    );
  }

  // If already logged in, show nothing (will redirect)
  if (user) {
    return null;
  }

  return (
    <div className="login-container">
      <div className="gradient-bg"></div>
      <div className="shape shape-1"></div>
      <div className="shape shape-2"></div>
      <div className="shape shape-3"></div>

      <div className="login-content">
        <div className="login-card">
          <div className="brand-section">
            <h1 className="brand-title">Welcome Back</h1>
            <p className="brand-subtitle">
              Sign in to continue to your account
            </p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Terms acceptance */}
          <div className="terms-acceptance">
            <label className="terms-label">
              <input
                type="checkbox"
                className="terms-checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <span>
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="terms-link">
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="terms-link">
                  Privacy Policy
                </Link>
              </span>
            </label>
          </div>

          {/* Login button */}
          <button
            className={`login-button ${isLoading ? "loading" : ""} ${!termsAccepted ? "disabled" : ""}`}
            onClick={handleLogin}
            disabled={isLoading || !termsAccepted}
            title={!termsAccepted ? "Please accept the Terms of Use and Privacy Policy to continue" : ""}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              <>Log in</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;